import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Button, Tag, Space, Popconfirm, Spin } from 'antd';
import { PlusOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { useGetMyPortalProjectsQuery, useDeletePortalProjectMutation } from '../../redux/api/compassPortal';
import { useGetIfUserIsAdminQuery } from '../../redux/api/user';
import CreateProjectModal from './modals/CreateProjectModal';

const statusColors = {
    'not-started': 'default',
    'in-progress': 'processing',
    'blocked': 'error',
    'complete': 'success',
};

const ProjectList = () => {
    const navigate = useNavigate();
    const [showCreateModal, setShowCreateModal] = useState(false);

    const selectedClient = useSelector(
        (state) => state.genericReporting.selectedDropdownValues['client_name']
    );
    const token = useSelector((state) => state.oauth.auth0Token);

    const { data: projects, isLoading } = useGetMyPortalProjectsQuery(undefined, {
        skip: !token,
        refetchOnMountOrArgChange: true,
    });
    const { data: isAdmin } = useGetIfUserIsAdminQuery(undefined, { skip: !token });
    const [deleteProject] = useDeletePortalProjectMutation();

    const getOverallStatus = (stages) => {
        if (!stages || stages.length === 0) return 'not-started';
        if (stages.every((s) => s.status === 'complete')) return 'complete';
        if (stages.some((s) => s.status === 'blocked')) return 'blocked';
        if (stages.some((s) => s.status === 'in-progress')) return 'in-progress';
        return 'not-started';
    };

    // Get unique client names for the filter
    const clientNames = [...new Set((projects || []).map((p) => p.client_name))].sort();

    const columns = [
        {
            title: 'Client',
            dataIndex: 'client_name',
            key: 'client_name',
            filters: clientNames.map((c) => ({ text: c, value: c })),
            onFilter: (value, record) => record.client_name === value,
            // Auto-filter to selected client if one is active
            ...(selectedClient ? { defaultFilteredValue: [selectedClient] } : {}),
        },
        {
            title: 'Project Name',
            dataIndex: 'project_name',
            key: 'project_name',
        },
        {
            title: 'Kick-off Date',
            dataIndex: 'kickoff_date',
            key: 'kickoff_date',
        },
        {
            title: 'Delivery Date',
            dataIndex: 'delivery_date',
            key: 'delivery_date',
        },
        {
            title: 'Status',
            key: 'status',
            render: (_, record) => {
                const status = getOverallStatus(record.stages);
                return <Tag color={statusColors[status]}>{status.replace('-', ' ')}</Tag>;
            },
        },
        {
            title: 'Created By',
            dataIndex: 'created_by',
            key: 'created_by',
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Space>
                    <Button
                        type="link"
                        icon={<EyeOutlined />}
                        onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/compass-portal/${record.project_id}`);
                        }}
                    >
                        View
                    </Button>
                    {isAdmin && (
                        <Popconfirm
                            title="Delete this project?"
                            description="This will permanently delete the project and all its data."
                            onConfirm={(e) => {
                                e?.stopPropagation();
                                deleteProject({ projectId: record.project_id });
                            }}
                            okText="Delete"
                            okType="danger"
                        >
                            <Button
                                type="link"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={(e) => e.stopPropagation()}
                            >
                                Delete
                            </Button>
                        </Popconfirm>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <div style={{ width: '100%' }}>
            <div className="compass-portal-header">
                <h2>Projects</h2>
                {isAdmin && (
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => setShowCreateModal(true)}
                    >
                        Create Project
                    </Button>
                )}
            </div>

            <Spin spinning={isLoading}>
                <Table
                    dataSource={projects || []}
                    columns={columns}
                    rowKey="project_id"
                    pagination={false}
                    locale={{ emptyText: 'No projects found' }}
                    onRow={(record) => ({
                        onClick: () => navigate(`/compass-portal/${record.project_id}`),
                        style: { cursor: 'pointer' },
                    })}
                />
            </Spin>

            <CreateProjectModal
                open={showCreateModal}
                onClose={() => setShowCreateModal(false)}
            />
        </div>
    );
};

export default ProjectList;
